const TERMINAL_JOB_STATUSES = new Set([
  'done',
  'completed_with_errors',
  'failed',
  'cancelled',
]);

export function isTerminalJobStatus(status) {
  return TERMINAL_JOB_STATUSES.has(status);
}

export async function pollChatJob({
  jobId,
  initialJob = null,
  loadJob,
  sleep,
  onProgress = null,
  isCancelled = () => false,
  maxPollsPerWait = 60,
  fastPollCount = 10,
  fastPollIntervalMs = 1000,
  slowPollIntervalMs = 5000,
  networkBackoffBaseMs = 1000,
  networkBackoffMaxMs = 15000,
  maxConsecutiveLoadFailures = 5,
}) {
  let lastActiveJob = initialJob || { job_id: jobId, status: 'running' };
  let consecutiveLoadFailures = 0;
  let successfulPolls = initialJob?.polling_deferred ? fastPollCount : 0;

  for (let attempt = 0; attempt < maxPollsPerWait; attempt += 1) {
    if (isCancelled()) return { ...lastActiveJob, polling_deferred: true };

    let job;
    try {
      job = await loadJob(jobId);
    } catch (error) {
      if (isCancelled()) return { ...lastActiveJob, polling_deferred: true };
      if (error?.retryable === false) throw error;
      consecutiveLoadFailures += 1;
      if (consecutiveLoadFailures >= maxConsecutiveLoadFailures) {
        return { ...lastActiveJob, polling_deferred: true };
      }
      if (attempt + 1 < maxPollsPerWait) {
        const backoffMs = Math.min(
          networkBackoffBaseMs * (2 ** (consecutiveLoadFailures - 1)),
          networkBackoffMaxMs
        );
        await sleep(backoffMs);
      }
      continue;
    }

    if (isCancelled()) return { ...lastActiveJob, polling_deferred: true };
    lastActiveJob = job;
    onProgress?.(job);
    consecutiveLoadFailures = 0;
    successfulPolls += 1;

    if (isTerminalJobStatus(job?.status)) {
      return job;
    }

    if (attempt + 1 < maxPollsPerWait) {
      await sleep(
        successfulPolls <= fastPollCount ? fastPollIntervalMs : slowPollIntervalMs
      );
    }
  }

  return {
    ...lastActiveJob,
    polling_deferred: true,
  };
}

export async function pollChatJobUntilTerminal(options) {
  const isCancelled = options.isCancelled || (() => false);
  const maxPollingChunks = options.maxPollingChunks ?? 2;
  let job = options.initialJob;

  for (let chunk = 0; chunk < maxPollingChunks; chunk += 1) {
    job = await pollChatJob({
      ...options,
      initialJob: job,
      isCancelled,
    });
    if (isCancelled() || isTerminalJobStatus(job?.status)) return job;
  }

  return { ...job, polling_deferred: true };
}
